import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, X, ChevronLeft, ChevronRight, Images, Play } from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";

const STATUSES = ["Tamamlanan", "Devam Eden"];

const EXTRA_CATEGORIES = ["Proje Danışmanlıkları", "Mimari Tasarım & Dekorasyon"];

const CATEGORY_KEYS = {
  "Konut": "konut",
  "Ticari": "ticari",
  "Fabrika": "fabrika",
  "Lojistik": "lojistik",
  "Proje Danışmanlıkları": "danismanlik",
  "Mimari Tasarım & Dekorasyon": "mimari",
};

const projTitle = (p, lang) =>
  (p.title_i18n && p.title_i18n[lang]) || (lang === "en" && p.title_en) || p.title;

const ytEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    let id = null;
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.hostname.includes("youtube.com")) {
      id = u.searchParams.get("v");
      if (!id && u.pathname.startsWith("/embed/")) id = u.pathname.replace("/embed/", "");
      if (!id && u.pathname.startsWith("/shorts/")) id = u.pathname.replace("/shorts/", "");
    }
    return id ? `https://www.youtube.com/embed/${id}` : null;
  } catch (e) { return null; }
};

const ytThumbUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    let id = null;
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.hostname.includes("youtube.com")) {
      id = u.searchParams.get("v");
      if (!id && u.pathname.startsWith("/embed/")) id = u.pathname.replace("/embed/", "");
      if (!id && u.pathname.startsWith("/shorts/")) id = u.pathname.replace("/shorts/", "");
    }
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  } catch (e) { return null; }
};

// Swipeable horizontal carousel with arrow nav + scroll-snap
const CarouselGrid = ({ items, onOpen, lang, statuses, viewLabel, catLabel, swipeLabel }) => {
  const railRef = React.useRef(null);
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(true);

  const updateNav = React.useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  React.useEffect(() => {
    updateNav();
    const el = railRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", updateNav);
    return () => {
      el.removeEventListener("scroll", updateNav);
      window.removeEventListener("resize", updateNav);
    };
  }, [updateNav, items]);

  const scrollByCards = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.clientWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step * 1.5, behavior: "smooth" });
  };

  return (
    <div className="relative" data-testid="portfolio-grid">
      <button
        onClick={() => scrollByCards(-1)}
        disabled={!canPrev}
        aria-label="Previous"
        data-testid="carousel-prev"
        className={`hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center border bg-[var(--bg)]/90 backdrop-blur-sm transition-all ${
          canPrev ? "border-white text-white hover:bg-white hover:text-black" : "border-[var(--border)] text-[var(--border)] cursor-not-allowed opacity-50"
        }`}
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={() => scrollByCards(1)}
        disabled={!canNext}
        aria-label="Next"
        data-testid="carousel-next"
        className={`hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center border bg-[var(--bg)]/90 backdrop-blur-sm transition-all ${
          canNext ? "border-white text-white hover:bg-white hover:text-black" : "border-[var(--border)] text-[var(--border)] cursor-not-allowed opacity-50"
        }`}
      >
        <ChevronRight size={20} />
      </button>

      <div
        ref={railRef}
        className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
      >
        <style>{`.carousel-rail::-webkit-scrollbar{display:none}`}</style>
        {items.map((p, i) => {
          const galleryCount = ((p.gallery && p.gallery.length) || 0) + ((p.youtube_urls && p.youtube_urls.length) || 0) || 1;
          const hasVideo = (p.youtube_urls && p.youtube_urls.length) > 0;
          return (
            <motion.article
              key={p.id}
              data-card
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 5) * 0.05 }}
              className="group relative overflow-hidden cursor-pointer snap-start shrink-0 w-[78vw] sm:w-[55vw] md:w-[42vw] lg:w-[28vw] xl:w-[22vw]"
              data-testid={`project-card-${p.id}`}
              onClick={() => onOpen(p)}
              style={{ aspectRatio: "3/4" }}
            >
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full bg-[#181818] flex items-center justify-center">
                  <Images size={40} className="text-white/15" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-500" />

              {p.status && p.status !== "Tamamlanan" && (
                <div className="absolute top-4 left-4 px-3 py-1 bg-[var(--primary)] text-white text-[10px] tracking-widest uppercase">
                  {statuses[p.status] || p.status}
                </div>
              )}
              {galleryCount > 1 && (
                <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 bg-black/50 backdrop-blur-sm border border-white/20 text-white text-[11px]">
                  {hasVideo ? <Play size={12} fill="white" /> : <Images size={12} />}
                  {galleryCount}
                </div>
              )}

              <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/70 mb-2">
                  {catLabel ? catLabel(p.category) : p.category}
                </div>
                <h3 className="font-display text-xl md:text-2xl font-medium text-white mb-2 leading-tight">
                  {projTitle(p, lang)}
                </h3>
                <div className="flex items-center gap-2 text-sm text-white/80">
                  <MapPin size={14} />
                  {p.location}
                </div>
                <div className="overflow-hidden mt-3 max-h-0 group-hover:max-h-20 transition-all duration-500">
                  <span className="inline-block text-[10px] tracking-[0.25em] uppercase text-white border-b border-white/60 pb-0.5">
                    {viewLabel} →
                  </span>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-center gap-1.5 md:hidden">
        {items.slice(0, Math.min(items.length, 8)).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-white/30" />
        ))}
        {items.length > 0 && <span className="ml-2 text-[10px] tracking-widest uppercase text-[var(--text-muted)]">← {swipeLabel || "Kaydır"} →</span>}
      </div>
    </div>
  );
};

const Portfolio = ({ onStatsReady }) => {
  const { t, lang } = useI18n();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all"); // all | category | status
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // project for lightbox
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    let mounted = true;
    api.get("/projects").then((res) => {
      if (!mounted) return;
      setProjects(res.data);
      if (onStatsReady) {
        const completed = res.data.filter((p) => p.status === "Tamamlanan").length;
        const ongoing = res.data.filter((p) => p.status === "Devam Eden").length;
        const cities = new Set(res.data.map((p) => p.location.split("-")[0].trim())).size;
        onStatsReady({ completed, ongoing, cities });
      }
    }).catch((e) => console.error(e)).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [onStatsReady]);

  useEffect(() => {
    const onFilterEvt = (e) => {
      const d = e.detail || {};
      if (d.category) setFilter(d.category);
      else if (d.status) setFilter(d.status);
    };
    window.addEventListener("portfolio-filter", onFilterEvt);
    return () => window.removeEventListener("portfolio-filter", onFilterEvt);
  }, []);

  const baseCategories = Array.from(new Set(projects.map((p) => p.category)));
  EXTRA_CATEGORIES.forEach((c) => { if (!baseCategories.includes(c)) baseCategories.push(c); });
  const categories = ["all", ...baseCategories];

  const filtered = projects.filter((p) => {
    if (filter === "all") return true;
    if (STATUSES.includes(filter)) return p.status === filter;
    return p.category === filter;
  });

  const openLightbox = (p) => { setActive(p); setImgIdx(0); };
  const closeLightbox = () => setActive(null);

  const catLabel = useCallback((c) => {
    const k = CATEGORY_KEYS[c];
    return (k && t.services[k] && t.services[k].title) || c;
  }, [t]);

  const mediaItems = active ? [
    ...(active.gallery || []).map((src) => ({ type: "img", src })),
    ...(active.youtube_urls || []).map((src) => ({ type: "yt", src, embed: ytEmbedUrl(src), thumb: ytThumbUrl(src) })),
  ] : [];
  const mediaCount = mediaItems.length || 1;

  const next = useCallback(() => {
    setImgIdx((i) => (i + 1) % mediaCount);
  }, [mediaCount]);
  const prev = useCallback(() => {
    setImgIdx((i) => (i - 1 + mediaCount) % mediaCount);
  }, [mediaCount]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, next, prev]);

  return (
    <section id="portfolio" className="relative py-24 md:py-32 bg-[var(--bg)]" data-testid="portfolio-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-12 md:mb-16 max-w-3xl"
        >
          <div className="overline mb-4">{t.portfolio.overline}</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white leading-[1.05] mb-6">
            {t.portfolio.title}
          </h2>
          <p className="text-base text-[var(--text-muted)] leading-relaxed">{t.portfolio.subtitle}</p>
        </motion.div>

        <div className="flex flex-wrap gap-2 md:gap-3 mb-10" data-testid="portfolio-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c}`}
              className={`px-4 md:px-5 py-2 text-[11px] md:text-xs tracking-widest uppercase border transition-all ${
                filter === c
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-[var(--text-muted)] border-[var(--border)] hover:text-white hover:border-white"
              }`}
            >
              {c === "all" ? t.portfolio.all : catLabel(c)}
            </button>
          ))}
          <div className="hidden md:block w-px bg-[var(--border)] mx-2" />
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-status-${s}`}
              className={`px-4 md:px-5 py-2 text-[11px] md:text-xs tracking-widest uppercase border transition-all ${
                filter === s
                  ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                  : "bg-transparent text-[var(--text-muted)] border-[var(--border)] hover:text-white hover:border-white"
              }`}
            >
              {t.statuses[s]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-[3/4] bg-[var(--surface)] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center text-[var(--text-muted)]" data-testid="portfolio-empty">
            {t.portfolio.empty}
          </div>
        ) : (
          <CarouselGrid
            items={filtered}
            onOpen={openLightbox}
            lang={lang}
            statuses={t.statuses}
            viewLabel={t.portfolio.viewGallery}
            catLabel={catLabel}
            swipeLabel={t.portfolio.swipe}
          />
        )}
      </div>

      {/* Lightbox / Project Modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex flex-col"
            data-testid="project-lightbox"
            onClick={(e) => { if (e.target === e.currentTarget) closeLightbox(); }}
          >
            {/* Header */}
            <div className="flex items-start justify-between px-6 lg:px-12 py-5 border-b border-white/10">
              <div className="max-w-3xl">
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/60 mb-1">
                  {active.category} · {t.statuses[active.status] || active.status}
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                  {projTitle(active, lang)}
                </h3>
                <div className="flex items-center gap-2 text-sm text-white/70 mt-1">
                  <MapPin size={14} />
                  {active.location}
                </div>
              </div>
              <button
                onClick={closeLightbox}
                className="p-2 text-white hover:text-[var(--silver)]"
                aria-label="Close"
                data-testid="lightbox-close"
              >
                <X size={24} />
              </button>
            </div>

            {/* Main media */}
            <div className="flex-1 relative flex items-center justify-center px-6 lg:px-20 py-6 overflow-hidden">
              <AnimatePresence mode="wait">
                {(() => {
                  const cur = mediaItems[imgIdx];
                  if (cur && cur.type === "yt" && cur.embed) {
                    return (
                      <motion.div
                        key={`yt-${imgIdx}`}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-full max-w-5xl aspect-video"
                        data-testid={`lightbox-video-${imgIdx}`}
                      >
                        <iframe
                          src={cur.embed}
                          title={active.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full border-0"
                        />
                      </motion.div>
                    );
                  }
                  return (
                    <motion.img
                      key={`img-${imgIdx}`}
                      src={(cur && cur.src) || active.image_url}
                      alt={active.title}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="max-h-full max-w-full object-contain"
                      data-testid={`lightbox-image-${imgIdx}`}
                    />
                  );
                })()}
              </AnimatePresence>

              {mediaItems.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 p-3 border border-white/20 bg-black/40 text-white hover:bg-white hover:text-black transition-colors"
                    aria-label="Previous"
                    data-testid="lightbox-prev"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 p-3 border border-white/20 bg-black/40 text-white hover:bg-white hover:text-black transition-colors"
                    aria-label="Next"
                    data-testid="lightbox-next"
                  >
                    <ChevronRight size={22} />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails + counter */}
            <div className="border-t border-white/10 px-6 lg:px-12 py-4">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="text-xs text-white/60 tracking-widest">
                  {imgIdx + 1} / {mediaItems.length || 1}
                </div>
                {active.description && (
                  <p className="hidden md:block text-xs text-white/70 max-w-2xl truncate">
                    {lang === "en" && active.description_en ? active.description_en : active.description}
                  </p>
                )}
              </div>
              {mediaItems.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2" data-testid="lightbox-thumbs">
                  {mediaItems.map((m, i) => (
                    <button
                      key={i}
                      onClick={() => setImgIdx(i)}
                      className={`relative shrink-0 w-20 h-14 overflow-hidden border-2 transition-all ${
                        i === imgIdx ? "border-white" : "border-transparent opacity-50 hover:opacity-100"
                      }`}
                      data-testid={`thumb-${i}`}
                    >
                      <img src={m.type === "yt" ? (m.thumb || "") : m.src} alt="" className="w-full h-full object-cover" />
                      {m.type === "yt" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Play size={16} className="text-white" fill="white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
